// Chat backups to the user's own cloud: Google Drive (the app's hidden
// app-data folder), iCloud (CloudKit, the user's private database), or a file
// the user keeps anywhere (iCloud Drive, Google Drive, a USB stick...).
// The backup is encrypted with the user's backup password before it leaves
// the device (history.ts packBackup), so the provider only sees ciphertext.
import { api } from "./api";

export type CloudKind = "google" | "icloud";

export interface CloudInfo {
  messages: number;
  created_at: number; // seconds
  size: number;
}

export interface Cloud {
  kind: CloudKind;
  label: string;
  /** Sign in (if needed). Call straight from a click: it may open a popup. */
  connect(): Promise<void>;
  info(account: string): Promise<CloudInfo | null>;
  upload(account: string, blob: Blob, meta: { messages: number; created_at: number }): Promise<void>;
  download(account: string): Promise<ArrayBuffer>;
}

interface CloudConfig {
  google_client_id?: string;
  cloudkit?: { container?: string; api_token?: string; environment?: string };
}

let config: Promise<CloudConfig> | null = null;
const cloudConfig = () => (config ??= api.config().then((c): CloudConfig => (c as { cloud?: CloudConfig }).cloud ?? {}, (): CloudConfig => ({})));

/** The providers this server is set up for. */
export async function clouds(): Promise<Cloud[]> {
  const c = await cloudConfig();
  const out: Cloud[] = [];
  if (c.google_client_id) out.push(googleDrive(c.google_client_id));
  if (c.cloudkit?.container && c.cloudkit.api_token) out.push(iCloud(c.cloudkit.container, c.cloudkit.api_token, c.cloudkit.environment || "production"));
  return out;
}

const fileName = (account: string) => `xmm-backup-${account}.xmmb`;

const scripts = new Map<string, Promise<void>>();
export function loadScript(src: string): Promise<void> {
  let p = scripts.get(src);
  if (!p) {
    p = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = src;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => (scripts.delete(src), reject(new Error("couldn't load " + new URL(src).host)));
      document.head.appendChild(s);
    });
    scripts.set(src, p);
  }
  return p;
}

// ---- Google Drive ------------------------------------------------------------------

const GSI = "https://accounts.google.com/gsi/client";
const DRIVE_SCOPE = "https://www.googleapis.com/auth/drive.appdata";

type TokenResponse = { access_token?: string; expires_in?: number; error?: string; error_description?: string };
type GoogleOAuth = {
  accounts: {
    oauth2: {
      initTokenClient(o: { client_id: string; scope: string; callback: (r: TokenResponse) => void; error_callback?: (e: { type?: string; message?: string }) => void }): {
        requestAccessToken(o?: { prompt?: string }): void;
      };
    };
  };
};

function googleDrive(clientId: string): Cloud {
  let token: { value: string; until: number } | null = null;
  void loadScript(GSI).catch(() => {}); // ready before the click that needs it

  async function connect() {
    if (token && token.until > Date.now() + 60_000) return;
    await loadScript(GSI);
    const g = (window as unknown as { google: GoogleOAuth }).google;
    token = await new Promise((resolve, reject) => {
      g.accounts.oauth2
        .initTokenClient({
          client_id: clientId,
          scope: DRIVE_SCOPE,
          callback: (r) => (r.access_token ? resolve({ value: r.access_token, until: Date.now() + (r.expires_in ?? 3600) * 1000 }) : reject(new Error(r.error_description || r.error || "Google sign-in failed"))),
          error_callback: (e) => reject(new Error(e.message || (e.type === "popup_closed" ? "Google sign-in was closed" : "Google sign-in failed"))),
        })
        .requestAccessToken({ prompt: "" });
    });
  }

  async function drive(url: string, init: RequestInit = {}) {
    if (!token) throw new Error("not signed in to Google");
    const res = await fetch(url, { ...init, headers: { ...(init.headers ?? {}), Authorization: "Bearer " + token.value } });
    if (res.status === 401) token = null;
    if (!res.ok) throw new Error(`Google Drive: ${res.status} ${(await res.text()).slice(0, 200)}`);
    return res;
  }

  async function find(account: string) {
    const q = encodeURIComponent(`name='${fileName(account)}' and trashed=false`);
    const r = await drive(`https://www.googleapis.com/drive/v3/files?spaces=appDataFolder&q=${q}&fields=files(id,size,appProperties)`);
    const { files } = (await r.json()) as { files: { id: string; size?: string; appProperties?: Record<string, string> }[] };
    return files[0] ?? null;
  }

  return {
    kind: "google",
    label: "Google Drive",
    connect,
    async info(account) {
      const f = await find(account);
      if (!f) return null;
      return { messages: Number(f.appProperties?.messages ?? 0), created_at: Number(f.appProperties?.created_at ?? 0), size: Number(f.size ?? 0) };
    },
    async upload(account, blob, meta) {
      const f = await find(account);
      const metadata = { name: fileName(account), appProperties: { messages: String(meta.messages), created_at: String(meta.created_at) }, ...(f ? {} : { parents: ["appDataFolder"] }) };
      const boundary = "xmm" + crypto.randomUUID();
      const body = new Blob([
        `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify(metadata)}\r\n`,
        `--${boundary}\r\nContent-Type: application/octet-stream\r\n\r\n`,
        blob,
        `\r\n--${boundary}--`,
      ]);
      const url = f ? `https://www.googleapis.com/upload/drive/v3/files/${f.id}?uploadType=multipart` : "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart";
      await drive(url, { method: f ? "PATCH" : "POST", headers: { "Content-Type": `multipart/related; boundary=${boundary}` }, body });
    },
    async download(account) {
      const f = await find(account);
      if (!f) throw new Error("no backup in your Google Drive");
      return (await drive(`https://www.googleapis.com/drive/v3/files/${f.id}?alt=media`)).arrayBuffer();
    },
  };
}

// ---- iCloud (CloudKit JS) ------------------------------------------------------------
// Needs a record type "XmmBackup" in the container (fields: file Asset,
// messages Int64, createdAt Int64); the development environment creates it on
// first save.

const CLOUDKIT = "https://cdn.apple-cloudkit.com/ck/2/cloudkit.js";
/** Where CloudKit draws its "Sign in with Apple ID" button. */
export const APPLE_SIGN_IN_ID = "apple-sign-in-button";

type CKRecord = { recordName: string; recordChangeTag?: string; fields: Record<string, { value: unknown }> };
type CKResponse = { hasErrors: boolean; errors: { ckErrorCode: string; reason?: string }[]; records: CKRecord[] };
type CKDatabase = { fetchRecords(names: string[]): Promise<CKResponse>; saveRecords(r: unknown[]): Promise<CKResponse> };
type CKContainer = { setUpAuth(): Promise<unknown>; whenUserSignsIn(): Promise<unknown>; privateCloudDatabase: CKDatabase };
type CloudKitJS = { configure(o: unknown): void; getDefaultContainer(): CKContainer };

function iCloud(container: string, apiToken: string, environment: string): Cloud {
  let ck: CKContainer | null = null;
  void loadScript(CLOUDKIT).catch(() => {});

  const recordName = (account: string) => "backup-" + account.replace(/[^\w.-]/g, "_");
  async function fetchRecord(account: string): Promise<CKRecord | null> {
    const r = await ck!.privateCloudDatabase.fetchRecords([recordName(account)]);
    if (r.hasErrors) {
      if (r.errors[0]?.ckErrorCode === "NOT_FOUND") return null;
      throw new Error("iCloud: " + (r.errors[0]?.reason || r.errors[0]?.ckErrorCode));
    }
    return r.records[0] ?? null;
  }

  return {
    kind: "icloud",
    label: "iCloud",
    async connect() {
      await loadScript(CLOUDKIT);
      const CK = (window as unknown as { CloudKit: CloudKitJS }).CloudKit;
      if (!ck) {
        CK.configure({ containers: [{ containerIdentifier: container, apiTokenAuth: { apiToken, persist: true, signInButton: { id: APPLE_SIGN_IN_ID, theme: "black" } }, environment }] });
        ck = CK.getDefaultContainer();
      }
      // Not signed in: CloudKit shows its button in #apple-sign-in-button.
      if (!(await ck.setUpAuth())) await ck.whenUserSignsIn();
    },
    async info(account) {
      const r = await fetchRecord(account);
      if (!r) return null;
      const f = r.fields;
      return { messages: Number(f.messages?.value ?? 0), created_at: Number(f.createdAt?.value ?? 0), size: Number((f.file?.value as { size?: number } | undefined)?.size ?? 0) };
    },
    async upload(account, blob, meta) {
      const cur = await fetchRecord(account);
      const r = await ck!.privateCloudDatabase.saveRecords([
        {
          recordType: "XmmBackup",
          recordName: recordName(account),
          ...(cur?.recordChangeTag ? { recordChangeTag: cur.recordChangeTag } : {}),
          fields: { file: { value: blob }, messages: { value: meta.messages }, createdAt: { value: meta.created_at } },
        },
      ]);
      if (r.hasErrors) throw new Error("iCloud: " + (r.errors[0]?.reason || r.errors[0]?.ckErrorCode));
    },
    async download(account) {
      const r = await fetchRecord(account);
      const url = (r?.fields.file?.value as { downloadURL?: string } | undefined)?.downloadURL;
      if (!url) throw new Error("no backup in your iCloud");
      const res = await fetch(url);
      if (!res.ok) throw new Error("iCloud download failed");
      return res.arrayBuffer();
    },
  };
}

// ---- a file the user keeps ------------------------------------------------------------

/** Save the backup file: the share sheet where there is one (pick iCloud
 * Drive, Google Drive, Files...), else a download. */
export async function saveBackupFile(blob: Blob, name: string) {
  const file = new File([blob], name, { type: "application/octet-stream" });
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  if (nav.canShare?.({ files: [file] }) && matchMedia("(pointer: coarse)").matches) {
    try {
      await nav.share({ files: [file], title: "xmm chat backup" });
      return;
    } catch (e) {
      if ((e as Error).name === "AbortError") throw new Error("Not saved");
    }
  }
  const a = document.createElement("a");
  a.href = URL.createObjectURL(file);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 60_000);
}
